import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, RendererFactory2 } from '@angular/core';

import { Subject, fromEvent, take, takeUntil } from 'rxjs';

import { OverlayWrapperService } from '../overlay-wrapper/overlay-wrapper.service';
import { WindowRefService } from '../window-ref/window-ref.service';

interface BackDropOptions {
  hostClasses?: string[];
}

export enum BackDropClass {
  FADE = 'fade',
  SHOW = 'show',
  MODAL_OPEN = 'modal-open',
  MODAL_BACKDROP = 'modal-backdrop'
}

enum CssProp {
  SCROLLBAR_WITH = '--scrollbar-width',
  PADDING_RIGHT = 'padding-right'
}

@Injectable({
  providedIn: 'root'
})
export class BackdropService {
  private readonly body = this.document.body;
  private readonly renderer = this.rendererFactory.createRenderer(null, null);
  private readonly cancelClose = new Subject<void>();

  private backDrop: HTMLElement;
  private closing = false;
  private removeOffsetCallback: () => void;

  constructor(
    private readonly rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly overlayWrapper: OverlayWrapperService,
    private readonly winRef: WindowRefService
  ) {}

  create({ hostClasses }: BackDropOptions = {}): void {
    this.backDrop ??= this.renderer.createElement('div');
    this.cancelClose.next();

    this.backDrop.className = '';
    [BackDropClass.MODAL_BACKDROP, BackDropClass.FADE, BackDropClass.SHOW, ...(hostClasses || [])].forEach(cls =>
      this.renderer.addClass(this.backDrop, cls)
    );

    if (!this.isOpen) {
      this.removeOffsetCallback = this.setPageScrollbarOffset(true, true);
      this.renderer.addClass(this.body, BackDropClass.MODAL_OPEN);
      this.renderer.appendChild(this.overlayWrapper.getWrapper(), this.backDrop);
    }
  }

  remove(): void {
    if (this.isOpen && !this.closing) {
      this.closing = true;

      fromEvent(this.backDrop, 'animationend')
        .pipe(take(1), takeUntil(this.cancelClose))
        .subscribe(_ => {
          this.renderer.removeChild(this.backDrop.parentElement, this.backDrop);
          this.renderer.removeClass(this.body, BackDropClass.MODAL_OPEN);
          this.removeOffsetCallback?.();
          this.closing = false;
        });

      this.renderer.removeClass(this.backDrop, BackDropClass.SHOW);
    }
  }

  /**
   * Based on NgBootstrap ScrollBar class, which cannot be used as it's not exported.
   * Additionally, the method sets CSS variable `--scrollbar-width` on body,
   * which can be used for a variety of style calculations.
   * @private
   */
  setPageScrollbarOffset(addStyle: boolean, addCssVar: boolean): () => void {
    const win: Window = this.winRef.nativeWindow;
    const scrollbarWidth = Math.abs(win.innerWidth - this.document.documentElement.clientWidth);
    addCssVar && this.renderer.setProperty(this.body, 'style', `${CssProp.SCROLLBAR_WITH}: ${scrollbarWidth}px`);

    if (addStyle && scrollbarWidth) {
      const actualPadding = parseFloat(win.getComputedStyle(this.body).paddingRight);
      this.renderer.setStyle(this.body, CssProp.PADDING_RIGHT, `${actualPadding + scrollbarWidth}px`);

      return () => this.renderer.setStyle(this.body, CssProp.PADDING_RIGHT, actualPadding ? `${actualPadding}px` : '');
    }

    return () => {};
  }

  private get isOpen(): boolean {
    return !!this.backDrop?.parentElement;
  }
}
